/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo} from 'react'
import {
    Alert,
    AlertDescription,
    AlertTitle,
    CloseButton,
    useMultiStyleConfig,
    useStyleConfig,
    useToast as useChakraToast
} from '@chakra-ui/react'

/**
 * Display a toast message on the screen.
 * This is a custom hook to handle showing toasts in the app, preventing duplicate toasts and to add action elements
 * to toasts when required. It supports all props supported by Chakra toast.
 *
 * @param {string} title Message text to be displayed in toast
 * @param {string} id - id provided to the toast to avoid duplicate toast ids, use it if multiple toasts are needed
 * @param {string} status Semantic state of the toast - success | error | info | warning
 * @param {node} action Optional component to be displayed in the toast (eg. Button to allow user to perform action)
 * @param {string} position The placement of the toast on screen
 * @param {number} duration The delay before the toast hides (in milliseconds)
 */
export function useToast(options) {
    const styles = useMultiStyleConfig('Toast', options)
    const linkStyles = useStyleConfig('Link', {variant: 'core'})
    // apply the container styles from the toast theme
    const toast = useChakraToast({containerStyle: styles.container, ...options})

    return useMemo(() => {
        const toastFn = ({
            title,
            description,
            id,
            status,
            action,
            position = 'bottom',
            duration = 20000,
            variant = 'toast',
            isClosable = true
        }) =>
            toast({
                id,
                title,
                description,
                status,
                isClosable,
                position,
                duration,
                variant,
                render: ({onClose}) => (
                    <Alert status={status} variant={variant}>
                        {title && (
                            <AlertTitle
                                sx={{a: linkStyles}}
                                dangerouslySetInnerHTML={{__html: title}}
                            ></AlertTitle>
                        )}
                        {description && <AlertDescription>{description}</AlertDescription>}
                        {action}
                        {isClosable && (
                            <CloseButton variant="toast" size="toast" onClick={onClose} />
                        )}
                    </Alert>
                )
            })
        toastFn.close = toast.close
        return toastFn
    }, [toast, linkStyles])
}
